import { useState } from "react";
import { Download, FileBarChart2, Loader2, Mail, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { COPPER } from "./VestaAuth";
import { useVestaMe } from "./VestaViewAs";
import { MailComposer } from "./VestaMail";

const n = (v, d = 2, u = "") => (v == null ? "—" : `${Number(v).toLocaleString("it-IT", { minimumFractionDigits: d, maximumFractionDigits: d, useGrouping: "always" })}${u}`);
const btn = "inline-flex items-center gap-1.5 rounded-full border border-neutral-700 px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest text-neutral-200 transition-colors hover:border-[#C17F59] hover:text-[#C17F59]";
const th = "px-2 py-1.5 text-left text-[10px] font-bold uppercase tracking-wider text-neutral-500";
const td = "px-2 py-1.5 whitespace-nowrap";

const Kpi = ({ l, v, testid }) => (
  <div className="rounded-lg bg-neutral-900/70 px-2.5 py-1.5" data-testid={testid}>
    <div className="text-[9px] font-bold uppercase tracking-widest text-neutral-500">{l}</div>
    <div className="truncate font-mono text-sm font-semibold text-white">{v}</div>
  </div>
);

const Fasce = ({ p }) => (
  <div className="flex h-1.5 w-24 overflow-hidden rounded-full bg-neutral-800" title={`F1 ${p.f1 ?? 0}% · F2 ${p.f2 ?? 0}% · F3 ${p.f3 ?? 0}%`}>
    <span style={{ width: `${p.f1 || 0}%`, background: "#E8B48F" }} /><span style={{ width: `${p.f2 || 0}%`, background: COPPER }} /><span style={{ width: `${p.f3 || 0}%`, background: "#7a4a30" }} />
  </div>
);

function Classifica({ rows, title, testid }) {
  return (
    <section data-testid={testid}>
      <div className="mb-1.5 text-[10px] font-bold uppercase tracking-widest" style={{ color: COPPER }}>{title}</div>
      <div className="overflow-x-auto rounded-xl border border-neutral-800">
        <table className="w-full text-xs"><thead><tr><th className={th}>Nome</th><th className={th}>Boll.</th><th className={th}>kWh</th><th className={th}>Min</th><th className={th}>Medio €/kWh</th><th className={th}>Max</th><th className={th}>F1/F2/F3</th></tr></thead>
          <tbody className="divide-y divide-neutral-900 text-neutral-200">{rows.map((x) => (
            <tr key={x.nome} data-testid={`${testid}-row`}><td className={`${td} font-semibold text-white`}>{x.nome}</td><td className={td}>{x.bollette}</td><td className={`${td} font-mono`}>{n(x.kwh, 0)}</td>
              <td className={`${td} font-mono`}>{n(x.prezzo_min, 3)}</td><td className={`${td} font-mono text-[#E8B48F]`}>{n(x.prezzo_medio, 3)}</td><td className={`${td} font-mono`}>{n(x.prezzo_max, 3)}</td><td className={td}><Fasce p={x.ripartizione_pct} /></td></tr>
          ))}</tbody></table>
      </div>
    </section>
  );
}

function Dettaglio({ rows, net }) {
  return (
    <section data-testid="bollette-dettaglio">
      <div className="mb-1.5 text-[10px] font-bold uppercase tracking-widest" style={{ color: COPPER }}>Bollette ({rows.length})</div>
      <div className="max-h-72 overflow-auto rounded-xl border border-neutral-800">
        <table className="w-full text-xs"><thead className="sticky top-0 bg-neutral-950"><tr><th className={th}>Cliente</th>{net && <th className={th}>Partner</th>}<th className={th}>Fornitore</th><th className={th}>Distributore</th><th className={th}>F1 kWh · €</th><th className={th}>F2 kWh · €</th><th className={th}>F3 kWh · €</th><th className={th}>€/kWh</th><th className={th}>Totale</th></tr></thead>
          <tbody className="divide-y divide-neutral-900 text-neutral-300">{rows.map((b) => (
            <tr key={b.id} data-testid={`bollette-row-${b.id}`}><td className={`${td} text-white`}>{b.cliente || "—"}</td>{net && <td className={td}>{b.partner || "—"}</td>}<td className={td}>{b.fornitore || "—"}</td><td className={td}>{b.distributore || "—"}</td>
              {["f1", "f2", "f3"].map((k) => <td key={k} className={`${td} font-mono`}>{n(b.fasce_kwh[k], 0)} · {n(b.costi_fascia_eur[k], 0)}</td>)}
              <td className={`${td} font-mono`}>{n(b.prezzo_medio_eur_kwh, 3)}</td><td className={`${td} font-mono text-[#E8B48F]`}>{n(b.costo_annuo_eur, 0, " €")}</td></tr>
          ))}</tbody></table>
      </div>
    </section>
  );
}

function Report({ d, net }) {
  const a = d.aggregato;
  if (!a.bollette) return <p className="text-sm text-neutral-500" data-testid="bollette-empty">Nessuna bolletta caricata nelle configurazioni.</p>;
  const rp = a.ripartizione_pct;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4">
        <Kpi l="Bollette" v={a.bollette} testid="bollette-kpi-n" /><Kpi l="kWh/anno" v={n(a.kwh_totali, 0)} testid="bollette-kpi-kwh" />
        <Kpi l="Prezzo medio" v={n(a.prezzo_medio, 3, " €/kWh")} testid="bollette-kpi-prezzo" /><Kpi l="F1 / F2 / F3" v={`${rp.f1 ?? 0} / ${rp.f2 ?? 0} / ${rp.f3 ?? 0} %`} testid="bollette-kpi-fasce" />
      </div>
      {d.commento && <div className="rounded-xl border border-[#C17F59]/30 bg-[#C17F59]/5 p-3" data-testid="bollette-commento">
        <div className="mb-1 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest" style={{ color: COPPER }}><Sparkles className="h-3.5 w-3.5" />Commento Ai VESTA</div>
        <p className="whitespace-pre-line text-xs leading-relaxed text-neutral-300">{d.commento}</p>
      </div>}
      <Classifica rows={a.fornitori} title="Classifica fornitori" testid="bollette-fornitori" />
      <Classifica rows={a.distributori} title="Classifica distributori" testid="bollette-distributori" />
      <Dettaglio rows={d.bollette} net={net} />
      {d.nota_prezzi && <p className="text-[11px] italic text-neutral-500" data-testid="bollette-nota">{d.nota_prezzi}</p>}
    </div>
  );
}

export function BolletteReport() {
  const p = useVestaMe()?.partner;
  const [open, setOpen] = useState(false);
  const [d, setD] = useState(null);
  const [mail, setMail] = useState(false);
  const params = p?.id ? { installer_id: p.id } : {};
  const net = !!p && !p.id;
  const load = () => { setOpen(true); setD(null); api.get("/rete/bollette/report", { params, timeout: 150000 }).then((r) => setD(r.data)).catch((e) => setD({ error: e.response?.data?.detail || "Report non disponibile" })); };
  const pdf = async () => {
    const w = window.open("", "_blank");
    try { const r = await api.get("/rete/bollette/report.pdf", { params, responseType: "blob", timeout: 150000 }); w.location.href = URL.createObjectURL(r.data); }
    catch { w?.close(); toast.error("PDF non disponibile"); }
  };
  if (!p) return null;
  return (
    <>
      <div className="-mt-4 mb-8 flex justify-end"><button type="button" className={btn} onClick={load} data-testid="bollette-report-btn"><FileBarChart2 className="h-4 w-4" />Report bollette</button></div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[88vh] max-w-4xl grid-cols-[minmax(0,1fr)] overflow-y-auto border-neutral-800 bg-neutral-950 text-white" data-testid="bollette-report-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading text-xl" style={{ color: COPPER }}><FileBarChart2 className="h-6 w-6" />Report bollette</DialogTitle>
            <DialogDescription className="text-[11px] uppercase tracking-widest text-neutral-500" data-testid="bollette-ambito">{net ? "Vista rete · tutti i Partner" : p.name} · fornitori, distributori e prezzi per fascia</DialogDescription>
          </DialogHeader>
          {d && !d.error && !!d.aggregato?.bollette && <div className="flex flex-wrap gap-2">
            <button type="button" className={btn} onClick={pdf} data-testid="bollette-pdf"><Download className="h-3.5 w-3.5" />Scarica PDF</button>
            <button type="button" className={btn} onClick={() => setMail(true)} data-testid="bollette-email"><Mail className="h-3.5 w-3.5" />Invia per email</button>
          </div>}
          {!d ? <div className="flex items-center gap-2 text-sm text-neutral-400" data-testid="bollette-loading"><Loader2 className="h-5 w-5 animate-spin" style={{ color: COPPER }} />Leggo le bollette e preparo l'analisi…</div>
            : d.error ? <p className="text-sm text-red-400" data-testid="bollette-error">{d.error}</p> : <Report d={d} net={net} />}
        </DialogContent>
      </Dialog>
      {mail && <MailComposer draft={{ subject: "Report bollette · fornitori e distributori", body: "Buongiorno,\n\nin allegato il report delle bollette con fornitori, distributori e prezzi dell'energia per fascia.\n\nCordiali saluti" }}
        onClose={() => setMail(false)} sendUrl="/rete/bollette/report/email" sendParams={params} attachment="Report bollette (PDF)" />}
    </>
  );
}
